import React, { useEffect, useState, useRef, useImperativeHandle, useMemo, forwardRef, useCallback } from 'react';
import BaseAPI from '../utils/BaseAPI';
import AntString from '../utils/AntString';
import AntLocalization from '../utils/AntLocalization';
import cs from '../utils/const.js';

const SAMPLE_QR_PATH = 'M1 1.5h7m4 0h2m1 0h7M1 2.5h1m5 0h1m4 0h1m2 0h1m5 0h1M1 3.5h1m1 0h3m1 0h1m1 0h3m3 0h1m1 0h3m1 0h1M1 4.5h1m1 0h3m1 0h1m1 0h1m1 0h1m1 0h1m1 0h1m1 0h3m1 0h1M1 5.5h1m1 0h3m1 0h1m1 0h2m1 0h2m1 0h1m1 0h3m1 0h1M1 6.5h1m5 0h1m1 0h2m2 0h1m1 0h1m5 0h1M1 7.5h7m1 0h1m1 0h1m1 0h1m1 0h7M9 8.5h1m1 0h3M1 9.5h1m1 0h5m5 0h1m1 0h5M2 10.5h2m1 0h2m4 0h1m1 0h1m2 0h1m2 0h1m1 0h1M1 11.5h1m1 0h5m3 0h2m1 0h1m2 0h4M3 12.5h1m2 0h1m3 0h2m4 0h6M2 13.5h1m2 0h3m2 0h3m1 0h1m2 0h1M9 14.5h1m2 0h5m1 0h1m1 0h2M1 15.5h7m2 0h1m2 0h1m1 0h2m1 0h3M1 16.5h1m5 0h1m1 0h1m1 0h5m2 0h2m1 0h1M1 17.5h1m1 0h3m1 0h1m1 0h1m1 0h1m1 0h1m4 0h1m1 0h1M1 18.5h1m1 0h3m1 0h1m1 0h2m2 0h1m2 0h2m1 0h1M1 19.5h1m1 0h3m1 0h1m1 0h2m1 0h1m1 0h1m4 0h1M1 20.5h1m5 0h1m8 0h1m1 0h2M1 21.5h7m1 0h1m2 0h1m1 0h1m2 0h1m2 0h1';

function SampleQRCode({ color, style }) {
    return (
        <svg
            data-qr-sample="true"
            aria-hidden="true"
            viewBox="0 0 23 23"
            shapeRendering="crispEdges"
            style={{ width: '100%', height: '100%', opacity: 0.45, ...style }}
        >
            <rect width="23" height="23" fill="#FFFFFF" />
            <path d={SAMPLE_QR_PATH} stroke={color || '#1D489A'} />
        </svg>
    );
}

export default forwardRef(function (props, ref) {
    const [qrKey, setQRKey] = useState('');
    const [qrMeta, setQRMeta] = useState(null);
    const [autoRefreshCount, setAutoRefreshCount] = useState(0);
    const [refreshBlocked, setRefreshBlocked] = useState(false);
    const [isGenerating, setIsGenerating] = useState(Boolean(props.initURL));
    const [generationError, setGenerationError] = useState(false);
    const [timeNum, setTimeNum] = useState(() => Math.max(1, Number(props.refreshSeconds || 15)));
    const hasManualRefreshPolicy = props.pauseAutoRefreshOnError === true || Number.isFinite(Number(props.maxAutoRefreshes));
    const continuousRefreshSeconds = Math.max(1, Number(props.refreshSeconds || 15));
    const continuousTimeRef = useRef(continuousRefreshSeconds);
    const requestInFlightRef = useRef(false);
    const requestSequenceRef = useRef(0);
    const retryTicksRef = useRef(0);

    const generateQRKey = useCallback((heartbeat) => {
        if (!props.initURL) return;
        if (!hasManualRefreshPolicy && requestInFlightRef.current) return;
        const requestSequence = ++requestSequenceRef.current;
        requestInFlightRef.current = true;
        retryTicksRef.current = 0;
        setIsGenerating(true);

        let settled = false;
        const watchdog = setTimeout(() => onGenerated({ error: 'QR_INIT_TIMEOUT' }), 8000);
        const onGenerated = (data) => {
            if (settled) return;
            settled = true;
            clearTimeout(watchdog);
            if (requestSequence !== requestSequenceRef.current) return;
            requestInFlightRef.current = false;
            setIsGenerating(false);
            if (data?.data?.key) {
                setQRKey(data.data.key);
                setQRMeta(data.data);
                setGenerationError(false);
                setRefreshBlocked(false);
                if (!hasManualRefreshPolicy) {
                    continuousTimeRef.current = continuousRefreshSeconds;
                    setTimeNum(continuousRefreshSeconds);
                }
            } else {
                setGenerationError(true);
                if (!hasManualRefreshPolicy) {
                    continuousTimeRef.current = 0;
                    setTimeNum(0);
                }
            }
        };

        if (props.onCustomGenerateQRKey) {
            try {
                const request = props.onCustomGenerateQRKey(props.initURL, onGenerated, {
                    heartbeat: Boolean(heartbeat),
                    previousKey: qrKey
                });
                if (request && typeof request.catch === 'function') request.catch((error) => onGenerated({ error }));
            } catch (error) {
                onGenerated({ error });
            }
            return;
        }

        try {
            const api = new BaseAPI();
            const url = heartbeat && props.heartbeatURL && qrKey ? props.heartbeatURL : props.initURL;
            const body = url === props.heartbeatURL ? { key: qrKey, ttl_seconds: qrMeta?.ttl_seconds } : {};
            const request = api.post(url, body, onGenerated);
            if (request && typeof request.catch === 'function') request.catch((error) => onGenerated({ error }));
        } catch (error) {
            onGenerated({ error });
        }
    }, [props.initURL, props.heartbeatURL, props.onCustomGenerateQRKey, qrKey, qrMeta?.ttl_seconds, hasManualRefreshPolicy, continuousRefreshSeconds]);
    const generateQRKeyRef = useRef(generateQRKey);
    generateQRKeyRef.current = generateQRKey;

    const generateDisplayURL = useCallback((url, _qrKey) => {
        if (!props.internalQR) return url;

        var BaseURL = localStorage.getItem(cs.System_Code + '-Sys_BaseURL');
        if (!BaseURL || BaseURL==='null' || BaseURL ==='undefined' ) BaseURL = cs.BaseURL;

        var queryURL = AntString.clean(BaseURL + url + `?key=${_qrKey}`);

        return queryURL;
    }, []);

    const [backgroundImg, setBackgroundImg] = useState(generateDisplayURL(props.displayURL));

    useEffect(() => {
        generateQRKeyRef.current(false);
    }, [props.initURL]);

    useEffect(() => {
        setBackgroundImg(generateDisplayURL(props.displayURL, qrKey));
    }, [props.displayURL, qrKey]);

    useEffect(() => {
        if (!hasManualRefreshPolicy) return;

        const ttl = Math.max(5, Number(qrMeta?.ttl_seconds || props.refreshSeconds || 15));
        const refreshAfter = Math.max(5, ttl - Number(props.refreshBeforeExpirySeconds || 3));
        if (!qrKey || refreshBlocked || generationError || isGenerating) return;
        const maxAutoRefreshes = Number.isFinite(Number(props.maxAutoRefreshes))
            ? Math.max(0, Number(props.maxAutoRefreshes))
            : null;
        const timer = setTimeout(() => {
            if (maxAutoRefreshes !== null && autoRefreshCount >= maxAutoRefreshes) {
                setRefreshBlocked(true);
                return;
            }
            setAutoRefreshCount((count) => count + 1);
            generateQRKey(true);
        }, refreshAfter * 1000);

        return () => {
            clearTimeout(timer);
        };
    }, [qrKey, qrMeta?.expires_at, props.refreshSeconds, props.refreshBeforeExpirySeconds, props.maxAutoRefreshes, generateQRKey, hasManualRefreshPolicy, refreshBlocked, generationError, isGenerating, autoRefreshCount]);

    useEffect(() => {
        if (hasManualRefreshPolicy) return;
        const interval = setInterval(() => {
            if (continuousTimeRef.current > 0) {
                const remaining = continuousTimeRef.current - 1;
                continuousTimeRef.current = remaining;
                setTimeNum(remaining);
                if (remaining === 0) generateQRKeyRef.current(false);
            } else if (!requestInFlightRef.current) {
                retryTicksRef.current += 1;
                if (retryTicksRef.current >= 3) generateQRKeyRef.current(false);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [hasManualRefreshPolicy, continuousRefreshSeconds, props.initURL]);

    useEffect(() => () => {
        requestSequenceRef.current += 1;
        requestInFlightRef.current = false;
    }, []);

    useEffect(() => {
        if (!hasManualRefreshPolicy) return;
        const fallbackExpiry = Math.floor(Date.now() / 1000) + Math.max(5, Number(props.refreshSeconds || 15));
        const expiresAt = Number(qrMeta?.expires_at || fallbackExpiry);
        const updateRemaining = () => setTimeNum(Math.max(0, expiresAt - Math.floor(Date.now() / 1000)));
        updateRemaining();

        const interval = setInterval(updateRemaining, 1000);

        return () => {
            clearInterval(interval);
        };
    }, [backgroundImg, qrMeta?.expires_at, props.refreshSeconds, hasManualRefreshPolicy]);

    const strTime = useMemo(() => {
        return `${AntLocalization.translateText({ vi: 'Còn', en: 'Remaining', fr: 'Restant', es: 'Restante', id: 'Tersisa', ja: '残り', zh: '剩余', ko: '남음', hi: 'शेष', ar: 'متبقي' })} ${timeNum} ${AntLocalization.translateText({ vi: 'giây', en: 'seconds', fr: 'secondes', es: 'segundos', id: 'detik', ja: '秒', zh: '秒', ko: '초', hi: 'सेकंड', ar: 'ثانية' })}`; //
    }, [timeNum]);

    const resetQR = useCallback(() => {
        requestSequenceRef.current += 1;
        requestInFlightRef.current = false;
        setQRKey('');
        setQRMeta(null);
        setAutoRefreshCount(0);
        setRefreshBlocked(false);
        setGenerationError(false);
        continuousTimeRef.current = continuousRefreshSeconds;
        setTimeNum(continuousRefreshSeconds);
        generateQRKeyRef.current(false);
    }, [continuousRefreshSeconds]);
    const showSample = refreshBlocked || !qrKey || generationError;
    const showRefreshOverlay = refreshBlocked || generationError || (!qrKey && !isGenerating);
    const resetOnQRPress = Boolean(props.resetOnPress || showRefreshOverlay);
    const canPressQR = resetOnQRPress && (!isGenerating || !hasManualRefreshPolicy);
    const renderedQRKey = showSample ? props.blockedSampleData : qrKey;
    const renderedBackgroundImg = generateDisplayURL(props.displayURL, renderedQRKey);

    return (
        <div
            className="ant-QRCode"
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                ...props.style
            }}
        >
            {props.bigTitle && (
                <div
                    style={{
                        // width: fit-content;
                        color: '#185ec0',
                        fontSize: '24px',
                        fontWeight: 500,
                        marginBottom: '10px'
                    }}
                >
                    {AntLocalization.translateText(props.bigTitle)}
                </div>
            )}
            {props.title && (
                <div
                    style={{
                        // width: fit-content;
                        color: '#185ec0',
                        fontSize: '18px',
                        fontWeight: 400
                    }}
                >
                    {AntLocalization.translateText(props.title)}
                </div>
            )}
            <div
                role={resetOnQRPress ? 'button' : undefined}
                tabIndex={resetOnQRPress ? 0 : undefined}
                aria-label={resetOnQRPress ? 'Reload QR code' : 'QR code'}
                aria-disabled={resetOnQRPress && !canPressQR ? true : undefined}
                onClick={canPressQR ? resetQR : undefined}
                onKeyDown={canPressQR ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        resetQR();
                    }
                } : undefined}
                style={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: props.imgStyle?.width || '70%',
                    padding: 0,
                    border: 0,
                    background: 'transparent',
                    cursor: resetOnQRPress ? (canPressQR ? 'pointer' : 'wait') : 'default',
                    // cardStyle: khung card (viền + bóng) bao quanh riêng ảnh QR, tiêu đề và đồng hồ nằm ngoài
                    ...props.cardStyle
                }}
            >
                {showSample ? (
                    <SampleQRCode color={props.refreshIconColor} style={props.imgStyle} />
                ) : (
                    <img
                        src={renderedBackgroundImg || backgroundImg}
                        alt="QR code"
                        onError={() => {
                            setGenerationError(true);
                            if (!hasManualRefreshPolicy) {
                                continuousTimeRef.current = 0;
                                retryTicksRef.current = 0;
                                setTimeNum(0);
                            }
                        }}
                        style={{
                            width: '100%',
                            ...props.imgStyle
                        }}
                    />
                )}
                {showRefreshOverlay && (
                    <span
                        aria-hidden="true"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(255, 255, 255, 0.56)',
                            borderRadius: props.imgStyle?.borderRadius || 8
                        }}
                    >
                        <i
                            className="dx-icon-refresh"
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 52,
                                height: 52,
                                borderRadius: '50%',
                                background: '#fff',
                                boxShadow: '0 2px 10px rgba(29, 72, 154, 0.22)',
                                color: props.refreshIconColor || '#2058ED',
                                fontSize: props.refreshIconSize || 26
                            }}
                        />
                    </span>
                )}
            </div>
            {props.showTimer !== false && qrKey && !showSample && (
                <div
                    style={{
                        color: '#87a0dee6',
                        fontSize: '14px',
                        fontWeight: 500
                    }}
                >
                    {strTime}
                </div>
            )}
            {/*  <div className="ant-pos-ARItemStatus-card-item-name-container">
                <div className="ant-pos-ARItemStatus-card-item-name">{item.item_name}</div>
            </div> */}
        </div>
    );
});
